import { 
  getAuth, 
  PhoneAuthProvider,
  RecaptchaVerifier,
  linkWithPhoneNumber,
  unlink,
  ConfirmationResult
} from "firebase/auth";
import { app } from "./firebase";
import { config } from "@/config/environment";

// Get the auth instance
const auth = getAuth(app);

// Get the reCAPTCHA site key from environment configuration
const recaptchaSiteKey = config.firebase.recaptcha.siteKey;

// Keep track of containers created to ensure unique IDs
let containerCounter = 0;
let recaptchaVerifier: RecaptchaVerifier | null = null;

// Check if phone provider is already linked and unlink if necessary
const unlinkPhoneProvider = async () => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error("You must be logged in to unlink phone authentication");
    }
    
    // Check if phone provider is already linked
    const phoneProviderId = PhoneAuthProvider.PROVIDER_ID;
    const hasPhoneProvider = currentUser.providerData.some(
      provider => provider.providerId === phoneProviderId
    );
    
    if (hasPhoneProvider) {
      console.log("Unlinking existing phone provider before linking new one");
      await unlink(currentUser, phoneProviderId);
      console.log("Successfully unlinked phone provider");
      return true;
    }
    
    return false;
  } catch (error) {
    console.error("Error unlinking phone provider:", error);
    throw error;
  }
};

// Clean up the reCAPTCHA resources
const cleanupRecaptcha = () => {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
      console.log("Cleared existing reCAPTCHA verifier");
    } catch (error) {
      console.warn("Error clearing reCAPTCHA verifier:", error);
    }
    recaptchaVerifier = null;
  }
  
  // Remove any visible reCAPTCHA badges
  const badges = document.querySelectorAll('.grecaptcha-badge');
  badges.forEach(badge => {
    try {
      badge.remove();
    } catch (e) {
      console.warn("Error removing reCAPTCHA badge:", e);
    }
  });
  
  // Try to reset global grecaptcha
  try {
    if (window.grecaptcha && typeof window.grecaptcha.reset === 'function') {
      window.grecaptcha.reset();
    }
  } catch (e) {
    console.warn("Error resetting global grecaptcha:", e);
  }
};

// Creates a fresh container for reCAPTCHA
const createFreshRecaptchaContainer = () => {
  cleanupRecaptcha();
  
  // Generate a unique ID for this container
  containerCounter++;
  const containerId = `recaptcha-container-${Date.now()}-${containerCounter}`;
  
  // Remove any existing container with this ID
  const existingContainer = document.getElementById(containerId);
  if (existingContainer) {
    existingContainer.remove();
  }
  
  // Create a new container and add it to the DOM
  const containerDiv = document.createElement('div');
  containerDiv.id = containerId;
  containerDiv.style.position = 'fixed';
  containerDiv.style.bottom = '-9999px';
  containerDiv.style.left = '-9999px';
  containerDiv.dataset.createdAt = Date.now().toString();
  
  document.body.appendChild(containerDiv);
  
  return containerId;
};

// Send verification code to phone number with standard reCAPTCHA
const sendPhoneVerificationCode = async (phoneNumber: string) => {
  try {
    console.log(`Attempting to send verification code to: ${phoneNumber}`);
    
    // Check if user is logged in
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("You must be logged in to verify your phone number");
    }
    
    // Create a fresh container for this verification attempt
    const containerId = createFreshRecaptchaContainer();
    console.log(`Using reCAPTCHA container ID: ${containerId}`);
    
    // Check if container element exists
    const containerElement = document.getElementById(containerId);
    if (!containerElement) {
      throw new Error(`Container element with ID '${containerId}' not found`);
    }
    
    // Create a new RecaptchaVerifier instance
    recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: (response: string) => {
        console.log("reCAPTCHA resolved");
      },
      'expired-callback': () => {
        console.log("reCAPTCHA expired");
        cleanupRecaptcha();
      }
    });
    
    console.log("RecaptchaVerifier created successfully");
    
    // Unlink existing phone provider if present
    await unlinkPhoneProvider();
    
    console.log("Linking phone number to user:", phoneNumber);
    
    // Link phone number to existing user
    try {
      const confirmationResult = await linkWithPhoneNumber(
        currentUser, 
        phoneNumber, 
        recaptchaVerifier
      );
      console.log("Phone verification code sent successfully");
      return confirmationResult;
    } catch (linkError) {
      console.error("Error linking phone number:", linkError);
      cleanupRecaptcha();
      throw linkError;
    }
  } catch (error) {
    console.error("Error sending verification code:", error);
    cleanupRecaptcha();
    throw error;
  }
};

// Verify phone number with code
const verifyPhoneCode = async (confirmationResult: ConfirmationResult, code: string) => {
  try {
    console.log("Verifying phone code:", code);
    const result = await confirmationResult.confirm(code);
    console.log("Phone verification successful");
    
    // Clean up the reCAPTCHA after successful verification
    cleanupRecaptcha();
    
    return result;
  } catch (error) {
    console.error("Error verifying code:", error);
    throw error;
  } finally {
    // Always ensure cleanup happens
    cleanupRecaptcha();
  }
};

export {
  sendPhoneVerificationCode,
  verifyPhoneCode,
  unlinkPhoneProvider,
  recaptchaSiteKey,
  cleanupRecaptcha
};
